<script setup lang="ts">import { computed as _computed, ref as _ref, toRef as _toRef } from 'vue';
const editingGiftIdeaData = __MACROS_toRef(__props, "giftIdea");
const originalMultiplaceGift = __MACROS_toRef(__props, "multiplaceGift");
const originalGift = __MACROS_toRef(__props, "gift");

import { toRef as __MACROS_toRef } from "vue";
import { isProduction } from '@shared/environment'
import TabPanelStepAmount from '@consumer/modules/customizer/steps/TabPanelStepAmount.vue'
import TabPanelStepDesign from '@consumer/modules/customizer/steps/TabPanelStepDesign.vue'
import TabPanelStepTagline from '@consumer/modules/customizer/steps/TabPanelStepTagline.vue'
import TabPanelStepPlaces from '@consumer/modules/customizer/steps/TabPanelStepPlaces.vue'
import Tab from '@consumer/modules/customizer/steps/Tab.vue'
import type GiftTemplate from '@consumer/types/GiftTemplate'
import { TabGroup, TabList, TabPanels, TabPanel } from '@headlessui/vue'
import type { Component } from 'vue'
import { createReusableTemplate, useUrlSearchParams } from '@vueuse/core'
import { useGeneralPlaces, updatePurchasedGift } from '@consumer/services/api/customizer'
import { showNotice } from '@consumer/services/flash'
import { customizer, landingPages } from '@consumer/api'
import { visit } from '@consumer/services/api'
import { useGiftTemplate } from '@consumer/composables/giftTemplate'
import { useSharedData } from '@consumer/composables/sharedData'
import { pushQuery, replaceQuery } from '@shared/url'
import { trackEvent } from '@shared/tracking'
import { isPhone, isDesktop } from '@consumer/utils/breakpoints'
import { cloneDeep } from '@consumer/helpers/object'
import type { SpecOptions } from '@consumer/composables/spec'
import { formatCurrency } from '@consumer/utils/currency'
import {
  setMaskColor,
  isCurrentMaskColor,
  defaultV3,
  v3WithImageBg,
  setTaglineUnlessCustom,
  validateTagline,
} from '@consumer/logic/giftly'
import { nextMaskFor } from '@consumer/assets/masks'
import { getPaletteFromImage } from '@consumer/helpers/color'
import type { Photo } from '@consumer/types/Photo'
import type { MultiplaceGift, PlaceDetail, GiftlyDesign, GiftSeries, GiftIdea } from '@consumer/types/serializers'
import PlacesTray from '@consumer/modules/customizer/steps/stepPlaces/PlacesTray.vue'
import MultiPlaceGiftIdeasApi from '@consumer/api/MultiPlaceGiftIdeasApi'
import V2ModalFullscreen from '@consumer/components/V2/V2ModalFullscreen.vue'
import V2HeaderModal from '@consumer/modules/v2/layout/HeaderModal.vue'
import smilingSunSrc from '@consumer/assets/icons/new-misc/gold-medal-smile.svg'
import { faSackDollar, faFillDrip, faLocationDot, faText, faTags } from '@fortawesome/pro-regular-svg-icons'
import type { IconDefinition } from '@fortawesome/fontawesome-common-types'
import { adminConsumerGiftSeries, adminConsumerGiftIdeas } from '@admin/api'
import { checkoutGift } from '@consumer/services/order'
import { openConfirmModal } from '@consumer/services/modals'
import { translateErrorToString } from '@shared/validation'
import { AxiosError } from 'axios'
import { notifyError } from '@shared/errors'
import { warnIfContainsCardWord } from '@consumer/utils/compliance'
import newCheckIcon from '@consumer/assets/icons/new/check.svg?svgicon'
import newCaretLeftIcon from '@consumer/assets/icons/new/caret-left.svg?svgicon'
import newCaretRightIcon from '@consumer/assets/icons/new/caret-right.svg?svgicon'
import TabPanelStepTags from './steps/TabPanelStepTags.vue'
import TabPanelStepMultiAmount from './steps/TabPanelStepMultiAmount.vue'

type StepName = 'Amount' | 'Design' | 'Place' | 'Tagline' | 'Tags'

export interface CustomizerModalProps {
  gift: GiftTemplate
  specOptions?: SpecOptions
  fromTools?: boolean
  onSave?: ((updatedGift: GiftTemplate) => Promise<void>)
  onSaveMultiplace?: ((updatedMultiplaceGift: MultiplaceGift) => Promise<void>)
  standalone?: boolean
  createOrEditMultiplace?: boolean
  multiplaceGift?: MultiplaceGift | null
  initialStep?: StepName
  originalTagline?: string
  backUrl?: string
  giftSeries?: GiftSeries | null
  validateTaglineOnMount?: boolean
  isMailableGift?: boolean
  giftId?: string
  createGiftIdea?: boolean
  giftIdea?: GiftIdea // Gift idea being edited
}

withDefaults(defineProps<CustomizerModalProps>(), { fromTools: false,validateTaglineOnMount: false,isMailableGift: false,createGiftIdea: false, })

const editingPurchasedGift = _computed(() => Boolean(__props.giftId))
const editingGiftIdea = _computed(() => Boolean(__props.giftIdea))
const isEditing = _computed(() => Boolean(__props.onSave) || Boolean(__props.onSaveMultiplace))

let open = _ref(true)
let isValidTagline = _ref(true)
let taglineError = _ref<string>('')
const isValidTitle = _ref(true)

const urlParams = useUrlSearchParams('history')
const urlParamsStep = urlParams.step as StepName | undefined

const customizerGift = _ref(cloneDeep(__props.giftIdea || __props.gift))
customizerGift.value.giftlyVersion ||= 3
customizerGift.value.giftlyMetadata ||= {}
customizerGift.value.giftlyMetadata.designName ||= defaultV3.name

const multiplaceGift = _ref(cloneDeep(__props.multiplaceGift))
const originalMultiplaceGiftSeries = cloneDeep(__props.multiplaceGift?.giftSeries)

interface Step {
  name: StepName
  icon: IconDefinition
  gaName: string
  component: Component
  title: string
}

const __$temp_1 = (useSharedData()),
  currentUser = _toRef(__$temp_1, 'currentUser'),
  currentTheme = _toRef(__$temp_1, 'currentTheme');

const amountTab = {
  component: __props.createOrEditMultiplace ? TabPanelStepMultiAmount : TabPanelStepAmount,
  name: 'Amount',
  icon: faSackDollar,
  title: 'Choose an Amount',
  gaName: 'nc_step_amount',
} as Step

const designTab = {
  component: TabPanelStepDesign,
  name: 'Design',
  icon: faFillDrip,
  title: 'Choose a Design',
  gaName: 'nc_step_design',
} as Step

const placesTab = {
  component: TabPanelStepPlaces,
  name: 'Place',
  icon: faLocationDot,
  title: 'Add one or more places',
  gaName: 'nc_step_place',
} as Step

const taglineTab = {
  component: TabPanelStepTagline,
  name: 'Tagline',
  icon: faText,
  title: 'Add a Catchy Tagline',
  gaName: 'nc_step_tagline',
} as Step

const tagsTab = {
  component: TabPanelStepTags,
  name: 'Tags',
  icon: faTags,
  title: 'Add some tags',
  gaName: 'nc_step_tags',
} as Step

const giftIdeaAmountTab = {
  component: TabPanelStepAmount,
  name: 'Amount',
  icon: faSackDollar,
  title: 'Choose an Amount',
  gaName: 'nc_step_amount',
} as Step

const steps: Step[] = _computed(() => {
  if (__props.giftSeries) {
    return [amountTab, designTab, tagsTab]
  }
  else if (__props.createGiftIdea || editingGiftIdea.value) {
    return [designTab, placesTab, taglineTab, giftIdeaAmountTab, tagsTab]
  }
  else if (__props.createOrEditMultiplace) {
    return [designTab, placesTab, taglineTab, amountTab, tagsTab]
  }
  else if (__props.fromTools) {
    return [amountTab, designTab, placesTab, taglineTab, tagsTab]
  }
  else if (editingPurchasedGift.value) {
    // When editing a purchased gift, hide the amount tab
    return [designTab, placesTab, taglineTab]
  }
  else {
    return [amountTab, designTab, placesTab, taglineTab]
  }
})

const isTaglineStepShown = _computed(() => steps.value.some(step => step.name === 'Tagline'))
const firstStep = _computed(() => urlParamsStep ?? __props.initialStep ?? (
  __props.createOrEditMultiplace || __props.createGiftIdea || editingGiftIdea.value ? 'Design' : 'Amount'
) as StepName)

let currentStepIndex = _ref<number>(Math.max(steps.value.findIndex(step => step.name === firstStep.value), 0))
const currentStep = _computed(() => steps.value[currentStepIndex.value] || steps.value[0])

// Start in the first step when re-opening the customizer.
watch(() => open.value, () => {
  currentStepIndex.value = 0
  replaceQuery({ step: null, gift: null })
})

function goToStepByIndex (index: number) {
  if (steps.value[index])
    currentStepIndex.value = index
}

function goToStep (name: StepName) {
  const index = steps.value.findIndex(step => step.name === name)
  goToStepByIndex(index)
}

const canSave = _computed(() => customizerGift.value.amount && isValidTagline.value && isValidTitle.value)

const __$temp_2 = (useGiftTemplate((customizerGift))),
  giftTemplateParam = _toRef(__$temp_2, 'giftTemplateParam');

const pushToHistory = !__props.onSave && !__props.onSaveMultiplace && !__props.giftSeries

if (pushToHistory) {
  onMounted(() => {
    pushQuery({ gift: giftTemplateParam.value }, { pathname: customizer.show.path(), component: 'Customizer' })
  })
}

watch(() => [currentStep.value, giftTemplateParam.value] as const, ([step, gift]) => {
  if (open.value) { replaceQuery({ step: step.name, gift }) }
})

const generalPlaces = useGeneralPlaces()

async function closeCustomizer () {
  trackEvent('click', 'nc_close')

  if (__props.createGiftIdea || editingGiftIdea.value) {
    // Confirm and close tab
    return openConfirmModal({
      header: editingGiftIdea.value ? 'Close & Discard Changes?' : 'Close & Discard Gift Idea?',
      content: editingGiftIdea.value
        ? 'Are you sure you want to close and discard your changes?'
        : 'Are you sure you want to close and discard this gift idea? All progress will be lost.',
      confirmLabel: 'Close & Discard',
      cancelLabel: 'Cancel',
      onConfirm: () => window.close(),
      onCancel: () => {},
    })
  }

  if (__props.backUrl || __props.standalone) {
    return visit(__props.backUrl || landingPages.index.path())
  }
  else {
    open.value = false
    if (pushToHistory) history.back()
  }
}

async function saveChanges () {
  await warnIfContainsCardWord(customizerGift.value.giftlyMetadata?.tagline, customizerGift.value.title)

  if (__props.giftId) {
    // Extract place IDs - general places use giftlyId, others use id
    const placeIds = customizerGift.value.places
      ?.map(place => place.giftlyId || place.id)
      ?.filter(Boolean)
      ?.map(String)

    try {
      await updatePurchasedGift(__props.giftId, {
        giftlyMetadata: customizerGift.value.giftlyMetadata,
        placeIds,
      })
      showNotice('Gift updated successfully! You can now close this window.')
    }
    catch (error) {
      notifyError(error)
      showNotice('There was an error updating the gift. Please try again.', { type: 'alert' })
    }
    return
  }

  if (multiplaceGift.value?.giftSeries && originalMultiplaceGiftSeries?.id !== multiplaceGift.value?.giftSeries?.id) {
    const confirmed = await openConfirmModal({
      header: 'Gift Series Update',
      content: `Adding this gift to the series will apply the ${multiplaceGift.value.giftSeries.name} defaults to this gift.`,
      confirmLabel: 'Save',
      cancelLabel: 'Cancel',
      onConfirm: () => true,
      onCancel: () => false,
    })
    if (!confirmed) return
  }

  if (!customizerGift.value.places?.length) {
    const anyPlace = generalPlaces.value?.find(place => place.name?.toLowerCase() === 'any place')
    if (anyPlace) {
      customizerGift.value.places = [anyPlace]
    }
  }

  trackEvent('click', 'nc_save')
  if (__props.createOrEditMultiplace && multiplaceGift.value) {
    if (customizerGift.value.giftlyMetadata) {
      multiplaceGift.value.places = customizerGift.value.places || []
      multiplaceGift.value.giftlyMetadata = customizerGift.value.giftlyMetadata
      multiplaceGift.value.title = customizerGift.value.title ?? ''
    }
    await __props.onSaveMultiplace?.(multiplaceGift.value)
  }
  else {
    await __props.onSave?.(customizerGift.value)
  }
  open.value = false
}

async function saveMultiplaceAndContinue () {
  await warnIfContainsCardWord(customizerGift.value.giftlyMetadata?.tagline, multiplaceGift.value?.title)

  // Show confirm only when saving to a series when it's not already in a series
  if (multiplaceGift.value?.giftSeries && !originalMultiplaceGiftSeries?.id) {
    const confirmed = await openConfirmModal({
      header: 'Adding to a Gift Series',
      content: `Adding this gift to the series will apply the ${multiplaceGift.value.giftSeries.name} defaults to this gift.`,
      confirmLabel: 'Save',
      cancelLabel: 'Cancel',
      onConfirm: () => true,
      onCancel: () => false,
    })
    if (!confirmed) {
      return
    }
  }

  const placeIds = customizerGift.value?.places?.map(place => place.giftlyId) || []
  const giftlyMetadata = customizerGift.value.giftlyMetadata

  const giftTags = multiplaceGift.value?.giftTags

  const { draftPageUrl, jid } = await MultiPlaceGiftIdeasApi.create({
    data: {
      amounts: multiplaceGift.value?.amounts,
      giftlyMetadata,
      placeIds,
      giftTags,
      giftSeriesId: multiplaceGift.value?.giftSeries?.id,
      title: multiplaceGift.value?.title ?? '',
    },
  })

  const url = jid ? `${draftPageUrl}?jid=${jid}` : draftPageUrl
  return visit(url)
}

async function saveGiftSeries () {
  const allTags = []
  if (multiplaceGift.value?.giftTags) {
    allTags.push(...multiplaceGift.value.giftTags)
  }
  else if (customizerGift.value.giftTags) {
    allTags.push(...customizerGift.value.giftTags)
  }

  await adminConsumerGiftSeries.update({
    params: __props.giftSeries,
    visit: false,
    data: {
      amounts: multiplaceGift.value?.amounts,
      giftly_metadata: customizerGift.value.giftlyMetadata,
      gift_tags: allTags,
    },
  })
  return adminConsumerGiftSeries.index()
}

async function saveGiftIdeaAndContinue () {
  const placeIds = customizerGift.value?.places?.map(place => place.giftlyId) || []
  const giftlyMetadata = customizerGift.value.giftlyMetadata

  const data = {
    amount: customizerGift.value.amount,
    title: customizerGift.value.title || giftlyMetadata?.tagline || 'Untitled',
    giftlyMetadata,
    places: placeIds,
  }

  if (editingGiftIdea.value) {
    await adminConsumerGiftIdeas.update({
      params: __props.giftIdea,
      visit: false,
      data,
    })

    await adminConsumerGiftIdeas.updateGiftTags({
      params: __props.giftIdea,
      visit: false,
      data: { giftTags: customizerGift.value.giftTags },
    })
  }
  else {
    const idea = await adminConsumerGiftIdeas.create({
      visit: false,
      data,
    })

    if (idea?.id && customizerGift.value.giftTags?.length) {
      await adminConsumerGiftIdeas.updateGiftTags({
        params: { id: idea.id },
        visit: false,
        data: { giftTags: customizerGift.value.giftTags },
      })
    }
  }

  return visit(adminConsumerGiftIdeas.index.path())
}

async function continueToCheckout () {
  trackEvent('click', 'nc_checkout')

  return checkoutGift(
    customizerGift.value,
    {
      specOptions: {
        ...__props.specOptions,
        purchaseMethod: 'customizer',
      },
      onAddToCart: closeCustomizer,
    },
    {
      validateTagline: isTaglineStepShown.value,
      onTaglineError: (error: string) => {
        isValidTagline.value = false
        taglineError.value = error
        goToStep('Tagline')
      },
    },
  )
}

const [DefineGiftlyPreview, GiftlyPreview] = createReusableTemplate<{
  class?: string
  giftlyClass?: string
  giftlyStyle?: any[]
}>({ inheritAttrs: false })
const [DefineCustomizerActions, CustomizerActions] = createReusableTemplate()
const [DefineGiftlyPalette, GiftlyPalette] = createReusableTemplate()

const showMobilePlacesTray = _computed(() => isDesktop.value || currentStep.value?.name === 'Place')

const showGiftlyPalette = _computed(() => currentStep.value?.name === 'Design' && giftlyPalette.value)

let highlightedStepIndex = _ref<number>()

function highlightNextStep () {
  highlightedStepIndex.value = currentStepIndex.value + 1
}

const scrollableEl = _ref<HTMLElement>()
watch(() => currentStepIndex.value, () => {
  scrollableEl.value?.scrollTo({ top: 0 })
  highlightedStepIndex.value = undefined
})

const CustomizerTools = import.meta.env.SSR || (isProduction && !Object.hasOwn(urlParams, 'tools'))
  ? 'template'
  : defineAsyncComponent(() => import('@consumer/modules/customizer/CustomizerTools.vue'))

let calculateMaskColor = false
let giftlyPalette = _ref<string[]>()

function switchDesign (design: GiftlyDesign) {
  giftlyPalette.value = undefined

  const meta = customizerGift.value.giftlyMetadata!
  meta.designName = design.id
  meta.backgroundImageUrl = design.backgroundImageUrl
  meta.maskColor = design.maskColor
  meta.maskName = design.maskName
  meta.logoColor = design.logoColor
  meta.backgroundColor = design.backgroundColor || null as any
  meta.backgroundImage = meta.unsplashImageId = null as any
}

function onNewDesign (photo: Photo | GiftlyDesign) {
  const metadata = customizerGift.value.giftlyMetadata!

  if (metadata.designName !== v3WithImageBg
    && photo.provider === 'unsplash') {
    metadata.maskName = 'giftly-v3-mask-1'
  }

  if (photo.provider === 'giftly') {
    switchDesign(photo)
  }
  else if (photo.provider === 'unsplash') {
    calculateMaskColor = true

    metadata.designName = v3WithImageBg
    metadata.backgroundImageUrl = photo.imageUrl
    metadata.backgroundImage = null as any
    metadata.unsplashImageId = photo.id
  }
  else if (photo.provider === 'user') {
    calculateMaskColor = true

    metadata.designName = v3WithImageBg
    metadata.backgroundImageUrl = photo.imageUrl
    metadata.backgroundImage = photo.id
    metadata.unsplashImageId = null as any
  }
}

async function onImageLoaded (event: Event) {
  if (customizerGift.value.giftlyMetadata!.designName === v3WithImageBg) {
    const img = event.currentTarget as HTMLImageElement
    giftlyPalette.value = getPaletteFromImage(img)
    if (calculateMaskColor || !customizerGift.value.giftlyMetadata?.maskColor) {
      setMaskColor(customizerGift.value, giftlyPalette.value[0])
      calculateMaskColor = false
    }
  }
}

function cycleMask (shift: number) {
  customizerGift.value.giftlyMetadata ||= {}
  customizerGift.value.giftlyMetadata.maskName = nextMaskFor(customizerGift.value.giftlyMetadata?.maskName, shift)
}

function onRemovePlace (place: PlaceDetail) {
  // Match on giftlyId for general places, id otherwise (same key the API uses).
  // Comparing giftlyId alone would drop every place with an undefined giftlyId
  // when removing a non-general place.
  const removedKey = place.giftlyId || place.id
  customizerGift.value.places = (customizerGift.value.places as PlaceDetail[] | undefined)
    ?.filter(p => p && (p.giftlyId || p.id) !== removedKey)
  if (!customizerGift.value.giftlyMetadata?.isCustomTagline) {
    setTaglineUnlessCustom(customizerGift.value)
  }
}

function onReorderPlaces () {
  if (!customizerGift.value.giftlyMetadata?.isCustomTagline) {
    setTaglineUnlessCustom(customizerGift.value)
  }
}

const customizerClass = computed(() => {
  if (currentStep.value.name === 'Tags'
    || (currentStep.value.name === 'Place'
        && customizerGift.value.places
        && customizerGift.value.places?.length > 1)) {
    return 'many-places'
  }
})

if (__props.validateTaglineOnMount) {
  onMounted(() => {
    if (__props.gift?.giftlyMetadata?.tagline) {
      validateTagline(__props.gift?.giftlyMetadata?.tagline, __props.gift).catch((error) => {
        if (error instanceof AxiosError && error.status === 422) {
          isValidTagline.value = false
          taglineError.value = translateErrorToString(error)
          goToStep('Tagline')
        }
      })
    }
  })
}
</script>

<template>
  <V2ModalFullscreen
    id="customizer-modal"
    :open="open"
    :appear="!standalone"
    name="customizer-modal"
    class="w-full h-full flex flex-col customizer-modal"
    :class="[customizerClass]"
    data-qa="Customizer"
    fullscreen
    fullscreenMobile
    hideCloseButton
    :pageTitle="{ text: 'Customize your Giftly' }"
    @close="closeCustomizer"
  >
    <DefineGiftlyPreview v-slot="{ class: classes, giftlyClass, giftlyStyle }">
      <div
        class="flex items-center relative justify-content-center"
        :class="classes"
      >
        <V1IconButton
          v-if="showGiftlyPalette"
          :icon="newCaretLeftIcon"
          :style="isDesktop && 'margin-left: -1rem; margin-right: -0.5rem;'"
          @click="cycleMask(-1)"
        />
        <div
          class="relative flex flex-col items-center w-full"
          style="max-width: calc(100vw - 48px * 2);"
          :style="!isDesktop && 'width: min(90vw, 40svh, 400px)'"
        >
          <div
            v-if="isDesktop && customizerGift.amount"
            class="absolute text-navy text-sm font-semibold text-center clickable
              pt-3 pb-7 -mb-4 bg-surface-yellow w-full rounded-t-v2lg"
            data-testid="amount-label"
            style="top: -2.5rem;"
            @click="goToStep('Amount')"
          >
            {{ formatCurrency(customizerGift.amount) }} gift
          </div>
          <GiftlyImage
            :gift="customizerGift"
            :overrideTagline="giftSeries?.name"
            class="w-full"
            :class="giftlyClass"
            :style="giftlyStyle"
            inCustomizer
            @clickAmount="goToStep('Amount')"
            @clickBackground="goToStep('Design')"
            @clickPlace="goToStep('Place')"
            @clickItem="goToStep('Tagline')"
            @imageLoaded="onImageLoaded"
          />
        </div>
        <V1IconButton
          v-if="showGiftlyPalette"
          :icon="newCaretRightIcon"
          :style="isDesktop && 'margin-right: -1rem; margin-left: -0.5rem;'"
          @click="cycleMask(1)"
        />
      </div>
      <div
        v-show="showMobilePlacesTray"
        class="w-full mx-auto px-0.5 lg:px-auto"
      >
        <PlacesTray
          :gift="customizerGift"
          :canReorder="currentStep.name === 'Place'"
          @removePlace="onRemovePlace"
          @reorderPlaces="onReorderPlaces"
        />
      </div>
    </DefineGiftlyPreview>

    <DefineGiftlyPalette>
      <div v-if="showGiftlyPalette" class="flex gap-4 items-center justify-center">
        <button
          v-for="color in giftlyPalette"
          :key="color"
          class="flex-shrink-0 flex items-center justify-center p-1"
          :style="isPhone ? { height: '48px', width: '48px' } : { height: '54px', width: '54px' }"
          type="button"
          @click="setMaskColor(customizerGift, color)"
        >
          <i
            class="rounded-full border-solid border-white border-2
              w-full h-full flex items-center justify-center shadow-md"
            :class="{ 'outline outline-2 outline-primary-base': isCurrentMaskColor(customizerGift, color) }"
            :style="{ background: color }"
          >
            <GIcon
              v-if="isCurrentMaskColor(customizerGift, color)"
              :svg="newCheckIcon"
              color="white"
              class="stroke-2 stroke-cap-round"
            />
          </i>
        </button>
        <input
          v-if="fromTools || (isDesktop && currentUser?.canCreateMultiplaceGifts)"
          type="color"
          :value="customizerGift.giftlyMetadata?.maskColor"
          @input="setMaskColor(customizerGift, ($event.target as HTMLInputElement)?.value)"
        >
      </div>
    </DefineGiftlyPalette>

    <DefineCustomizerActions>
      <GPromiseButton
        v-if="isEditing || editingPurchasedGift"
        class="w-full"
        :disabled="!canSave"
        :size="isDesktop ? 'large' : 'medium'"
        label="Save Changes"
        primary
        @click="saveChanges"
      />
      <GPromiseButton
        v-else-if="giftSeries"
        class="w-full"
        :size="isDesktop ? 'large' : 'medium'"
        name="nc_save_and_continue"
        primary
        label="Save Gift Series"
        @click="saveGiftSeries"
      />
      <GPromiseButton
        v-else-if="createGiftIdea || editingGiftIdea"
        class="w-full"
        :size="isDesktop ? 'large' : 'medium'"
        name="nc_save_gift_idea"
        primary
        :label="editingGiftIdea ? 'Save Changes' : 'Save & Continue'"
        @click="saveGiftIdeaAndContinue"
      />
      <GPromiseButton
        v-else-if="createOrEditMultiplace"
        class="w-full"
        :size="isDesktop ? 'large' : 'medium'"
        name="nc_save_and_continue"
        primary
        label="Save & Continue"
        @click="saveMultiplaceAndContinue"
      />
      <GPromiseButton
        v-else
        :disabled="!canSave"
        class="w-full"
        :size="isDesktop ? 'large' : 'medium'"
        name="nc_go_to_checkout"
        primary
        label="Continue"
        @click="continueToCheckout"
      />
    </DefineCustomizerActions>

    <template #header>
      <header id="v2-header" class="bg-white flex flex-col !border-none" v-bind="$attrs">
        <V2HeaderModal :currentTheme="currentTheme" @close="closeCustomizer">
          <h1 class="py-5 flex-grow flex items-center justify-center lg:hidden text-xl">
            <span
              v-if="!isDesktop && customizerGift.amount && !createOrEditMultiplace && !editingPurchasedGift"
              class="text-navy clickable"
              data-testid="amount-label"
              @click="goToStep('Amount')"
            >
              {{ formatCurrency(customizerGift.amount!) }} Gift
            </span>
            <template v-else>
              {{ editingPurchasedGift ? 'Edit Gift' : 'Customize your Giftly' }}
            </template>
          </h1>
          <h1 class="hidden lg:block text-sm leading-none font-medium text-v2-tertiary">
            {{ editingPurchasedGift ? 'Edit Gift' : 'Customize your Giftly' }}
          </h1>
          <CustomizerTools v-if="isDesktop" :gift="customizerGift"/>
        </V2HeaderModal>
        <div
          v-if="editingPurchasedGift"
          class="w-full px-4 py-3 mb-4 bg-surface-blue border-b border-grey-primary text-sm text-center font-semibold"
        >
          You are editing a purchased gift
        </div>
      </header>
    </template>

    <div
      v-if="!isDesktop"
      class="w-full flex flex-col bg-white"
      :class="!showGiftlyPalette && 'pb-2'"
    >
      <GiftlyPreview
        class="self-center w-max"
        :giftlyStyle="[{ boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.2)' }]"
      />
      <GiftlyPalette v-show="showGiftlyPalette" class="mt-2 mb-1"/>
    </div>
    <div
      ref="scrollableEl"
      class="overflow-y-scroll lg:overflow-y-auto flex-grow flex flex-col bg-white h-full"
      style="scrollbar-gutter: stable;"
    >
      <main
        class="flex w-full flex-grow max-w-customizer-modal mx-auto"
        :class="isDesktop
          ? 'gap-6 items-start justify-center'
          : 'gap-4 flex-col items-stretch'
        "
      >
        <TabGroup
          :selectedIndex="currentStepIndex"
          as="div"
          class="w-full mx-auto flex flex-col min-h-full"
          style="max-width: 660px;"
          @change="goToStepByIndex"
        >
          <TabList
            class="flex flex-col items-center w-full relative sticky bg-white z-1 pt-1"
            :style="{ top: isDesktop ? '0px' : '-1px' }"
          >
            <div class="w-full absolute bottom-0">
              <div class="w-full border-t-1 border-solid border-grey-400"/>
            </div>
            <div
              class="grid content-center w-full justify-center"
              data-qa="customize-tabs"
              :class="[isPhone ? 'gap-2 px-2 text-sm' : 'gap-1', `grid-cols-${steps.length}` ]"
            >
              <Tab
                v-for="step, index in steps"
                :key="step.name"
                :highlighted="index === highlightedStepIndex && !isEditing"
                :name="step.gaName"
                :label="step.name"
                :icon="step.icon"
              />
            </div>
          </TabList>
          <TabPanels
            as="div"
            class="tab-panels flex-grow relative mt-auto flex flex-col lg:mb-4"
          >
            <TabPanel
              v-for="step in steps"
              :key="step.name"
              :unmount="false"
              class="
                flex flex-col items-center bg-surface-grey border-1 border-grey-primary border-t-0
                mx-auto
                px-6 lg:px-5 lg:pb-5 sm:rounded-b-v2lg
                outline-none w-full
              "
              :class="isPhone ? 'flex-grow pb-24' : 'pb-6'"
            >
              <h1
                class="text-center"
                :class="isPhone ? 'text-lg my-4' : 'text-2xl my-5'"
                data-testid="tab-title"
              >
                {{ step.title }}
              </h1>
              <div
                v-if="editingPurchasedGift && isMailableGift"
                class="w-full mb-4 p-4 bg-surface-yellow rounded-v2md font-semibold text-sm"
              >
                <strong>Note:</strong> This gift has been mailed. Changes made here
                will not be reflected in the physical card.
              </div>
              <component
                :is="step.component"
                :gift="customizerGift"
                class="flex-grow w-full mx-auto"
                :isActive="currentStep.name === step.name"
                :multiplaceGift="multiplaceGift"
                :createOrEditMultiplace="createOrEditMultiplace"
                :allowBackgroundUpload="Boolean(multiplaceGift) || fromTools"
                :fromTools="fromTools"
                :originalTagline="originalTagline"
                :taglineError="taglineError"
                :editedGiftSeries="giftSeries"
                :giftSeries="giftSeries"
                :createGiftIdea="createGiftIdea"
                @validTagline="isValidTagline = $event"
                @validTitle="isValidTitle = $event"
                @stepComplete="highlightNextStep"
                @newImage="onNewDesign"
              />
            </TabPanel>
          </TabPanels>
        </TabGroup>
        <div
          v-if="isDesktop"
          class="flex flex-col sticky top-0 z-1 lg:flex-shrink-0"
          style="width: 415px;"
        >
          <GiftlyPreview class="mt-10 mb-2"/>

          <GiftlyPalette class="mb-2"/>

          <div
            class="customizer-actions flex flex-col items-stretch justify-center"
            :class="giftlyPalette ? 'my-2' : 'my-3'"
          >
            <CustomizerActions/>

          </div>
          <div
            v-if="!createOrEditMultiplace"
            class="flex justify-center items-center gap-2 text-sm font-semibold text-navy"
          >
            <img :src="smilingSunSrc" height="42" width="42">
            100% satisfaction guarantee
          </div>
        </div>
      </main>
    </div>

    <template v-if="!isDesktop" #actions>
      <CustomizerActions/>
    </template>
  </V2ModalFullscreen>
</template>

<style lang="scss">
#customizer-modal {
  .g-dialog-content {
    overflow: hidden;
  }

  .g-dialog-content-wrapper {
    display: flex;
    flex-direction: column;
    max-height: 100vh;
    padding: 0;
  }

  &.many-places {
    @media (max-width: 768px) {
      .g-dialog-content {
        overflow: auto;
      }

      .g-dialog-content-wrapper {
        display: block;
      }
    }
  }

  .giftly-logo {
    &__themed {
      @media only screen and (max-width: 1023px) {
        background-image: none !important;
      }

      @media only screen and (min-width: 1024px) {
        @apply bg-no-repeat bg-contain pl-15;

        background-size: 56px 56px;
      }
    }
  }

  &:not(.many-places) {
    .v2-modal-fullscreen-content-wrapper {
      @apply flex flex-col overflow-hidden;
    }
  }
}

.max-w-customizer-modal {
  max-width: 1024px;
}
</style>

<style scoped lang="scss">
.customizer-actions .g-button {
  --width: auto;

  min-width: max(25%, 160px);
  padding: 0 4rem;
}
</style>
